import { createContext, useContext } from 'react';
import { createValueStore, type IValueStore } from '@srm/ui/lib/valueStore';
import { initialControlState } from '@srm/screen-sharing/light';
import type { ScreenShareApi } from './useScreenShare';
import { createPresenceBus } from './supportPresence';
import { emptySupportChat } from './supportChat';
import { createMicLevel, createSpeaking, initialSupportVoice } from './supportVoice';
import { inertSupportSound } from './supportSound';

export interface IScreenShareContextValue extends ScreenShareApi {
    /** Показ экрана возможен в этой вкладке (во вкладке «от имени» — нет) */
    available: boolean;
    /** Место полосы поддержки в оболочке смонтировано; без него провайдер рисует полосу сам. Возвращает отписку */
    registerBarSlot: () => () => void;
    /** Панель чата раскрыта (кнопка «Чат» в полосе) */
    chatOpened: boolean;
    setChatOpened: (opened: boolean) => void;
    /**
     * Набранный в чате текст: переживает сворачивание панели, пропадает с концом показа. Стор, а не поле контекста:
     * каждая буква перерисовывает только панель чата, а не полосу и всех потребителей показа
     */
    chatDraft: IValueStore<string>;
}

/** Приставка заголовка вкладки, пока экран показывается: видно и из соседней вкладки. */
export const SCREEN_SHARE_TITLE_PREFIX = '● Показ экрана · ';

const noop = () => undefined;

/** Без провайдера (стори, тесты частей оболочки) показа нет. */
export const inertScreenShare: IScreenShareContextValue = {
    available: false,
    active: false,
    status: '',
    error: '',
    control: initialControlState,
    connection: null,
    viewerName: null,
    participants: [],
    annotated: false,
    actions: [],
    quality: null,
    restricted: false,
    cropped: false,
    transport: 'webrtc',
    captureArea: undefined,
    chat: emptySupportChat,
    voice: initialSupportVoice,
    feedback: null,
    suspended: false,
    resuming: false,
    restoring: false,
    resumeError: '',
    reselect: false,
    annotationsAllowed: true,
    gesture: null,
    diagnostics: null,
    micLevel: createMicLevel(),
    speaking: createSpeaking(),
    presence: createPresenceBus(),
    start: () => Promise.resolve(),
    startAgain: () => Promise.resolve(),
    restore: () => Promise.resolve(),
    continueShare: () => Promise.resolve(),
    stop: noop,
    allowControl: noop,
    revokeControl: noop,
    dismissError: noop,
    setAnnotationsAllowed: noop,
    runGesture: noop,
    dismissGesture: noop,
    sendCursor: noop,
    sendClickMark: noop,
    clearAnnotations: noop,
    sendChat: () => false,
    retryChat: noop,
    chatTyping: noop,
    markChatRead: noop,
    onChatIncoming: () => noop,
    toggleMic: () => Promise.resolve(),
    resumeVoice: noop,
    sound: inertSupportSound,
    dismissFeedback: noop,
    holdFeedback: noop,
    sendFeedback: () => Promise.resolve(),
    previewDiagnostics: () => null,
    sendDiagnostics: () => Promise.resolve(),
    declineDiagnostics: noop,
    registerBarSlot: () => noop,
    chatOpened: false,
    setChatOpened: noop,
    chatDraft: createValueStore(''),
};

export const ScreenShareContext = createContext<IScreenShareContextValue>(inertScreenShare);

export const useScreenShareContext = () => useContext(ScreenShareContext);

/**
 * Только «идёт показ»: шапке больше ничего не нужно, а полный контекст меняется на каждое сообщение чата и статус связи.
 */
export const ScreenShareActiveContext = createContext(false);

export const useScreenShareActive = () => useContext(ScreenShareActiveContext);

/**
 * Только «показ экрана возможен в этой вкладке» (во вкладке «от имени» и без провайдера — нет): шапке для подсказки
 * про показ, без подписки на весь контекст.
 */
export const ScreenShareAvailableContext = createContext(false);

export const useScreenShareAvailable = () => useContext(ScreenShareAvailableContext);
